import React, { Fragment } from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";

import { useLanguageContext } from "../../contexts/language-context";

/**
 * StateData
 * @typedef {Object} StateData
 * @property {string} id - required unique ide for the link.
 * @property {string} enName - required name to show as link for the users.
 * @property {string} enUrl - required intern link of the state page.
 * @property {string} esName - required name to show as link for the users.
 * @property {string} esUrl - required intern link of the state page.
 */

/**
 * This is the component to render the states links inside the section with states like in the page /business-banking/small-business.
 *
 * @param {Object} props - The object containing props.
 * @param {string} props.class - classes applied to the span container of the list of links.
 * @param {boolean} props.hasTexasLink - if this is true we will show a link for Texas.
 * @param {boolean} props.darkBackground - if this is true the text will be white.
 * @param {StateData[]} props.stateData - required array with the states data to show.
 * @returns {React.JSX.Element} Returns the JSX component to render.
 */
const ServiceStatesLinks = ({
  class: className = "",
  hasTexasLink = true,
  darkBackground = false,
  stateData = [
    {
      id: "service-state-link-arizona",
      enName: "Arizona",
      enUrl: "/locations/arizona",
      esName: "Arizona",
      esUrl: "/es/sucursales/arizona"
    },
    {
      id: "service-state-link-california",
      enName: "California",
      enUrl: "/locations/california",
      esName: "California",
      esUrl: "/es/sucursales/california"
    },
    {
      id: "service-state-link-idaho",
      enName: "Idaho",
      enUrl: "/locations/idaho",
      esName: "Idaho",
      esUrl: "/es/sucursales/idaho"
    },
    {
      id: "service-state-link-nevada",
      enName: "Nevada",
      enUrl: "/locations/nevada",
      esName: "Nevada",
      esUrl: "/es/sucursales/nevada"
    },
    {
      id: "service-state-link-new-mexico",
      enName: "New Mexico",
      enUrl: "/locations/new-mexico",
      esName: "Nuevo Mexico",
      esUrl: "/es/sucursales/nuevo-mexico"
    },
    {
      id: "service-state-link-oregon",
      enName: "Oregon",
      enUrl: "/locations/oregon",
      esName: "Oregon",
      esUrl: "/es/sucursales/oregon"
    },
    {
      id: "service-state-link-texas",
      enName: "Texas",
      enUrl: "/locations/texas",
      esName: "Texas",
      esUrl: "/es/sucursales/texas"
    },
    {
      id: "service-state-link-utah",
      enName: "Utah",
      enUrl: "/locations/utah",
      esName: "Utah",
      esUrl: "/es/sucursales/utah"
    },
    {
      id: "service-state-link-washington",
      enName: "Washington",
      enUrl: "/locations/washington",
      esName: "Washington",
      esUrl: "/es/sucursales/washington"
    }
  ]
}) => {
  const isSpanish = useLanguageContext();
  let stateDataLocal = stateData;

  if (!hasTexasLink) {
    stateDataLocal = stateDataLocal.filter((n) => n.enName !== "Texas");
  }

  return (
    <span className={className}>
      {stateDataLocal.map((obj, index) => (
        <Fragment key={index}>
          <Link id={obj.id} className={darkBackground ? "text-white" : ""} to={isSpanish ? obj.esUrl : obj.enUrl}>
            {isSpanish ? obj.esName : obj.enName}
          </Link>
          {index === stateDataLocal.length - 2
            ? // Append ", and" before the last state
              `${isSpanish ? ", y " : ", and "}`
            : index === stateDataLocal.length - 1
            ? // Append nothing after the last state
              ""
            : // Append ", " after each state
              ", "}
        </Fragment>
      ))}
    </span>
  );
};
export default ServiceStatesLinks;

ServiceStatesLinks.propTypes = {
  class: PropTypes.string,
  hasTexasLink: PropTypes.bool,
  darkBackground: PropTypes.bool,
  stateData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      enName: PropTypes.string.isRequired,
      enUrl: PropTypes.string.isRequired,
      esName: PropTypes.string.isRequired,
      esUrl: PropTypes.string.isRequired
    })
  ).isRequired
};
